/** Mini-mapa pro dashboard — Leaflet + CartoDB Dark + same custom piny.
 *  Klik na celou mapu → /map. */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Ic } from "./icons";
import {
  fetchMapPins,
  fetchPartnerVehicles,
  geocodePins,
  type MapPin,
  type PartnerVehiclePin,
} from "@/lib/fetchers";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";

const DE_CENTER: [number, number] = [51.0, 10.5];
const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const DARK_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CARTO';

function pinIcon(kind: MapPin["kind"]): L.DivIcon {
  if (kind === "done") {
    return L.divIcon({
      className: "portal-map-pin",
      html: `<div style="width:6px;height:6px;border-radius:999px;border:2px solid var(--text-3);box-shadow:0 0 0 1px rgba(0,0,0,0.5);"></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });
  }
  const tone =
    kind === "active" ? "var(--gold)" : kind === "planned" ? "var(--gold-lo)" : "var(--blue)";
  const halo =
    kind === "active"
      ? `<span style="position:absolute;left:-7px;top:-7px;width:24px;height:24px;border-radius:999px;background:var(--gold);opacity:0.25;animation:portal-pulse 1.6s ease-out infinite;"></span>`
      : "";
  return L.divIcon({
    className: "portal-map-pin",
    html: `<div style="position:relative;">${halo}<div style="position:relative;width:10px;height:10px;border-radius:999px;background:${tone};border:2px solid ${tone};box-shadow:0 0 0 2px rgba(0,0,0,0.5);"></div></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  });
}

function vehicleIcon(): L.DivIcon {
  return L.divIcon({
    className: "portal-map-pin",
    html:
      '<div style="width:22px;height:22px;border-radius:6px;background:var(--bg-elevated);border:1px solid var(--gold);display:flex;align-items:center;justify-content:center;color:var(--gold);box-shadow:0 0 0 3px rgba(201,169,107,0.15);">' +
      '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2" y="7" width="11" height="9" rx="1"/><path d="M13 10h5l3 3v3h-8"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>' +
      "</div>",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function FitToVisible({ pins, vehicles }: { pins: MapPin[]; vehicles: PartnerVehiclePin[] }) {
  const map = useMap();
  const all = useMemo(() => {
    const pts: [number, number][] = [];
    pins.forEach((p) => {
      if (typeof p.lat === "number" && typeof p.lng === "number") pts.push([p.lat, p.lng]);
    });
    vehicles.forEach((v) => {
      if (typeof v.lat === "number" && typeof v.lng === "number") pts.push([v.lat, v.lng]);
    });
    return pts;
  }, [pins, vehicles]);
  if (all.length === 0) return null;
  if (all.length === 1) {
    map.setView(all[0], 10);
  } else {
    map.fitBounds(L.latLngBounds(all), { padding: [30, 30] });
  }
  return null;
}

export function MiniMap({ height = 480 }: { height?: number }) {
  const pinsQ = useQuery({
    queryKey: queryKeys.mapPins,
    queryFn: async () => {
      const raw = await fetchMapPins();
      return geocodePins(raw);
    },
    staleTime: 5 * 60 * 1000,
  });
  const vehiclesQ = useQuery({
    queryKey: queryKeys.mapVehicles,
    queryFn: fetchPartnerVehicles,
    refetchInterval: 30 * 1000,
  });

  const pins = (pinsQ.data || []).filter(
    (p) => typeof p.lat === "number" && typeof p.lng === "number",
  );
  const vehicles = (vehiclesQ.data || []).filter(
    (v) => typeof v.lat === "number" && typeof v.lng === "number",
  );

  return (
    <div
      className="card"
      style={{
        height,
        position: "relative",
        overflow: "hidden",
        padding: 0,
      }}
    >
      <MapContainer
        center={DE_CENTER}
        zoom={5.4}
        style={{ position: "absolute", inset: 0, background: "#06090D" }}
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={false}
      >
        <TileLayer attribution={DARK_ATTR} url={DARK_TILES} />
        <FitToVisible pins={pins} vehicles={vehicles} />
        {pins.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.kind)} />
        ))}
        {vehicles.map((v) => (
          <Marker key={v.vehicle_id} position={[v.lat, v.lng]} icon={vehicleIcon()} />
        ))}
      </MapContainer>
      <Link
        to="/map"
        style={{
          position: "absolute",
          right: 12,
          top: 12,
          zIndex: 500,
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 12.5,
          color: "var(--text)",
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Ic.Map size={12} /> {t.dashboard.actionsMap}
        <Ic.ArrowRight size={12} />
      </Link>
      <div
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          zIndex: 500,
          fontSize: 11,
          color: "var(--text-3)",
          background: "rgba(11,15,20,0.7)",
          padding: "4px 8px",
          borderRadius: 6,
          backdropFilter: "blur(4px)",
        }}
      >
        {pins.length + vehicles.length} {t.map.layerActive.toLowerCase()} ·{" "}
        {vehicles.length > 0 ? `${vehicles.length} Live` : "Karte"}
      </div>
    </div>
  );
}
